import { ChangeEvent } from './events.js';
import EventEmitter from 'eventemitter3';
import { Slice } from 'prosemirror-model';
import { Selection, EditorState, Plugin as ProseMirrorPlugin, } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { undo, redo, closeHistory } from 'prosemirror-history';
import { createParagraphNear } from 'prosemirror-commands';
import { invalidReasonAttributeName } from './constants.js';
import { getMarkType, getHTMLFromFragment, getNodeFromHTML, getSliceFromHTML, truthy, } from './utils/index.js';
import { objectIncludes } from './utils/object.js';
import { isEmptyDoc } from './utils/doc.js';
import { getNodeType } from './utils/node.js';
import { SetAttrsStep } from './Steps.js';
import { SelectionCommands } from './Selection.js';
import { deprecate, renameFnWarn } from './utils/deprecate.js';
import { DOMParser } from './utils/parser.js';
export class EditorCoreCommands extends EventEmitter {
    constructor() {
        super();
        this.composingState = 0 /* ComposingState.end */;
        this.readonly = false;
        this.dispatchHook = (tr) => {
            if (tr.docChanged && this.readonly) {
                return false;
            }
            // --story=873412287 历史栈不应该记录 composition 过程
            if (this.view.composing) {
                this.composingState =
                    this.composingState === 0 /* ComposingState.end */
                        ? 1 /* ComposingState.first */
                        : 2 /* ComposingState.continues */;
            }
            if (this.composingState === 2 /* ComposingState.continues */) {
                tr.setMeta('appendedTransaction', tr);
            }
            return true;
        };
        this.transformHTMLHook = (html) => html;
        /** @deprecated use setNodeAttr */
        this.setBlockAttr = deprecate(this.setNodeAttr, renameFnWarn('setBlockAttr'));
        /** @deprecated use patchNodeAttr */
        this.patchBlockAttr = deprecate(this.patchNodeAttr, renameFnWarn('patchBlockAttr'));
        this.sliceFromHTML = (originHtml) => {
            const html = this.transformHTMLHook(originHtml);
            return getSliceFromHTML(html, this.view.state.schema);
        };
        this.htmlFromSlice = (slice) => getHTMLFromFragment(slice.content, this.view.state.schema);
        // composingState 重置回 end 不能依靠 dispatchHook，
        // 因为如连续打字多次上屏的场景全过程 tr 都处于 composing
        this.on('compositionend', () => {
            this.composingState = 0 /* ComposingState.end */;
        });
    }
    setReadOnly(readonly) {
        this.readonly = readonly;
        this.emit('readonly', this.isReadOnly());
        return this;
    }
    isReadOnly() {
        return this.readonly;
    }
    get doc() {
        return this.view.state.doc;
    }
    getState() {
        return this.view.state;
    }
    getJSON() {
        return this.view.state.doc.toJSON();
    }
    getHTML() {
        return getHTMLFromFragment(this.view.state.doc.content, this.view.state.schema);
    }
    /**
     * 重设内容。isReplace 能够保留编辑历史，默认不保留
     */
    setHTML(originHtml, isReplace = false) {
        const { schema, doc, tr } = this.view.state;
        const html = this.transformHTMLHook(originHtml);
        const newDoc = getNodeFromHTML(html, schema);
        if (isReplace) {
            this.view.dispatch(tr.replace(0, doc.content.size, new Slice(newDoc.content, 0, 0)));
        }
        else {
            const newState = this.setDoc(newDoc);
            this.emit('change', new ChangeEvent(newState, {
                // TODO 测试和 collab 的兼容
                steps: [],
                docs: [],
                docChanged: true,
                selectionSet: false,
            }));
        }
        return this;
    }
    setDoc(newDoc) {
        const newState = EditorState.create({
            doc: newDoc,
            plugins: this.view.state.plugins,
        });
        this.view.updateState(newState);
        return newState;
    }
    focus() {
        this.view.focus();
        return this;
    }
    hasFocus() {
        return this.view.hasFocus();
    }
    undo() {
        undo(this.view.state, this.view.dispatch);
    }
    canUndo() {
        return undo(this.view.state);
    }
    redo() {
        redo(this.view.state, this.view.dispatch);
    }
    canRedo() {
        return redo(this.view.state);
    }
    /**
     * 将入参函数中的所有动作合并，触发最多一次 change
     */
    batch(commands) {
        const tr = this.createBatchedTr(commands);
        if (tr) {
            this.view.dispatch(tr);
        }
        return this;
    }
    can(commands) {
        let ret = false;
        this.hookDispatch((tr) => {
            ret || (ret = tr.docChanged || tr.storedMarksSet || tr.selectionSet);
            return false;
        }, commands);
        return ret;
    }
    /**
     * 强制让下一个操作的历史栈记录独立，不与相近的其他操作合并
     */
    closeHistory() {
        this.once('predispatch', (tr) => {
            closeHistory(tr);
            this.once('predispatch', (tr) => closeHistory(tr));
        });
        return this;
    }
    selection(from, to) {
        const selectionCommands = new SelectionCommands(this.view, {
            htmlFromSlice: this.htmlFromSlice,
            sliceFromHTML: this.sliceFromHTML,
        });
        if (typeof from === 'number') {
            selectionCommands.set(from, to);
        }
        else if (from === true) {
            selectionCommands.insertEmptyParagraphIfGap();
        }
        return selectionCommands;
    }
    createParagraphNear() {
        this.execute(createParagraphNear);
    }
    /** 判断当前文档是否为空 */
    isEmpty() {
        return isEmptyDoc(this.view.state.doc);
    }
    /** 替换指定范围的全部内容，传入 html 或 slice */
    replace(from, to = from, slice = Slice.empty) {
        return this.dispatch((tr) => {
            if (slice instanceof Slice) {
                tr.replace(from, to, slice);
            }
            else {
                tr.replace(from, to, this.sliceFromHTML(slice));
            }
        });
    }
    /** 替换指定范围的全部内容，传入插件和节点属性 */
    replaceWith(from, to = from, plugOrName, attrs = {}) {
        return this.dispatch((tr) => {
            const nodeType = getNodeType(plugOrName, this.view.state.schema);
            tr.replaceWith(from, to, nodeType.create(attrs));
        });
    }
    /** 替换某一范围的文字 */
    replaceText(from, to, replacement, marks) {
        var _a;
        const { state, dispatch } = this.view;
        const usedMarks = marks === null || marks === void 0 ? void 0 : marks.map((m) => {
            var _a;
            return 'name' in m
                ? (_a = getMarkType(m.name, this.view.state.schema)) === null || _a === void 0 ? void 0 : _a.create(m.attrs)
                : m;
        }).filter(truthy);
        dispatch(state.tr.replaceWith(from, to, state.schema.text(replacement, usedMarks !== null && usedMarks !== void 0 ? usedMarks : (_a = state.doc.resolve(from).nodeAfter) === null || _a === void 0 ? void 0 : _a.marks)));
        return this;
    }
    /** 删除指定范围的全部内容 */
    delete(from, to) {
        return this.dispatch((tr) => tr.delete(from, to));
    }
    /** 删除指定范围的指定插件的所有实例 */
    deleteAll(plugOrName) {
        const targetName = typeof plugOrName === 'string' ? plugOrName : plugOrName.name;
        return this.dispatch((tr) => {
            const nodeInfos = [];
            tr.doc.nodesBetween(0, this.doc.content.size, (node, pos) => {
                if (node.type.name === targetName) {
                    nodeInfos.push({ pos, nodeSize: node.nodeSize });
                }
            });
            nodeInfos
                .reverse()
                .forEach((info) => tr.delete(info.pos, info.pos + info.nodeSize));
        });
    }
    /** 在指定位置插入文字 */
    insertText(pos, text) {
        return this.dispatch((tr) => tr.insertText(text, pos));
    }
    /**
     * 刷新，会触发所有插件的 decorate 重新执行。
     * 更推荐使用 refreshPlugin 指定要刷新的插件，以提高性能
     */
    refresh(metas) {
        return this.dispatch((tr) => {
            if (metas) {
                const metasObj = typeof metas === 'string' ? { [metas]: true } : metas;
                Object.keys(metasObj).forEach((k) => tr.setMeta(k, metasObj[k]));
            }
            tr.setMeta('addToHistory', false).setMeta('ex:refresh', true);
        });
    }
    /**
     * 刷新特定的插件，触发其 decorate 的重新执行
     * @param [force=true] 是否跳过 debounce 逻辑
     */
    refreshPlugin(plugOrName, metas, force = true) {
        return this.dispatch((tr) => {
            tr.setMeta("ex:targetPlugin" /* ExTrMetaKeys.targetPlugin */, typeof plugOrName === 'string' ? plugOrName : plugOrName.name);
            if (force) {
                tr.setMeta("ex:targetPluginForce" /* ExTrMetaKeys.targetPluginForce */, true);
            }
            if (metas) {
                const metasObj = typeof metas === 'string' ? { [metas]: true } : metas;
                Object.keys(metasObj).forEach((k) => tr.setMeta(k, metasObj[k]));
            }
            tr.setMeta('addToHistory', false).setMeta('ex:refresh', true);
        });
    }
    /**
     * 下一次操作不添加到 history 中
     */
    noHistory() {
        this.once('predispatch', (tr) => {
            tr.setMeta('addToHistory', false);
        });
        return this;
    }
    meta(metas) {
        this.once('predispatch', (tr) => {
            const metasObj = typeof metas === 'string' ? { [metas]: true } : metas;
            Object.keys(metasObj).forEach((k) => tr.setMeta(k, metasObj[k]));
        });
        return this;
    }
    /**
     * 设置某位置的节点的全部属性
     */
    setNodeAttr(pos, attrs) {
        const { dispatch, state: { tr }, } = this.view;
        tr.step(new SetAttrsStep(pos, attrs));
        dispatch(tr);
        return this;
    }
    /**
     * 修改某位置的节点的部分属性
     */
    patchNodeAttr(pos, attrs) {
        const { dispatch, state: { doc, tr }, } = this.view;
        const node = doc.nodeAt(pos);
        const oldAttrs = (node === null || node === void 0 ? void 0 : node.attrs) || {};
        const newAttrs = Object.assign({}, oldAttrs, attrs);
        tr.step(new SetAttrsStep(pos, newAttrs));
        dispatch(tr);
        return this;
    }
    /**
     * 找到第一个插件的节点实例位置
     */
    getPosByPlugin(plugOrName) {
        const targetName = typeof plugOrName === 'string' ? plugOrName : plugOrName.name;
        const { state: { doc }, } = this.view;
        let ret = -1;
        doc.nodesBetween(0, doc.content.size, (node, pos) => {
            if (ret === -1 && node.type.name === targetName) {
                ret = pos;
            }
            return ret === -1;
        });
        return ret;
    }
    /**
     * 查找第一个有指定属性的块的位置
     */
    getPosByAttr(key, value, options = {}) {
        const { state: { doc }, } = this.view;
        const from = options.from || 0;
        const to = options.to || doc.content.size;
        const compareEq = options.compareEq || Object.is;
        let ret = -1;
        doc.nodesBetween(from, to, (node, pos) => {
            if (ret === -1 && compareEq(node.attrs[key], value)) {
                ret = pos;
            }
            return ret === -1;
        });
        return ret;
    }
    getNodesByTypeName(name) {
        const { state: { doc }, } = this.view;
        const nodes = [];
        doc.nodesBetween(0, doc.content.size, (node) => {
            if (node.type.name === name) {
                nodes.push(node);
                return false;
            }
            return true;
        });
        return nodes;
    }
    /**
     * 获取某一范围内的纯文本，缺省范围为全文
     */
    textBetween(from, to, blockSeparator, leafText) {
        const { doc } = this.view.state;
        return doc.textBetween(from !== null && from !== void 0 ? from : 0, to !== null && to !== void 0 ? to : doc.content.size, blockSeparator, leafText);
    }
    /**
     * 获取指定点位指定行内样式的最大连续外延范围
     */
    getMarkRange(pos, plugOrName) {
        const $pos = typeof pos === 'number' ? this.view.state.doc.resolve(pos) : pos;
        const markType = getMarkType(plugOrName, this.view.state.schema);
        const mark = $pos.marks().find((m) => m.type === markType);
        if (!mark) {
            return null;
        }
        let from = $pos.pos;
        let { nodeBefore } = $pos;
        while (nodeBefore) {
            if (nodeBefore.marks.find((m) => m.type === markType && objectIncludes(m.attrs, mark.attrs))) {
                from -= nodeBefore.nodeSize;
                nodeBefore = $pos.doc.resolve(from).nodeBefore;
            }
            else {
                break;
            }
        }
        let to = $pos.pos;
        let { nodeAfter } = $pos;
        while (nodeAfter) {
            if (nodeAfter.marks.find((m) => m.type === markType && objectIncludes(m.attrs, mark.attrs))) {
                to += nodeAfter.nodeSize;
                nodeAfter = $pos.doc.resolve(to).nodeAfter;
            }
            else {
                break;
            }
        }
        return { from, to };
    }
    removeMark(from, to, plugOrName) {
        return this.dispatch((tr) => {
            tr.removeMark(from, to, plugOrName
                ? getMarkType(plugOrName, this.view.state.schema)
                : undefined);
        });
    }
    // 底层方法
    execute(command) {
        return command(this.view.state, this.view.dispatch, this.view);
    }
    // 底层方法
    dispatch(mutateTr) {
        const { tr } = this.view.state;
        mutateTr(tr);
        this.view.dispatch(tr);
        return this;
    }
    /**
     * 返回第一个由插件设置的 invalid reason，或 null 表示 valid
     */
    valid(scrollInvalidDOMIntoView = false) {
        const eltFirstReason = this.view.dom.querySelector(`[${invalidReasonAttributeName}]`);
        if (eltFirstReason) {
            if (scrollInvalidDOMIntoView) {
                eltFirstReason.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return eltFirstReason.getAttribute(invalidReasonAttributeName);
        }
        return null;
    }
    isComposing() {
        return this.composingState !== 0 /* ComposingState.end */;
    }
    shortcut(key) {
        return this.keymaps[key];
    }
    /** selection().isActive 的简写 */
    isActive(plug) {
        return this.selection().isActive(plug);
    }
    /**
     * 返回一个对象，这个对象会尽力记住传入的位置，
     * 并根据编辑器内容变化不断地更新它
     */
    rememberPos(oldPos) {
        let pos = oldPos;
        const onChange = (e) => {
            e.steps.forEach((step) => {
                pos = step.getMap().map(pos);
            });
        };
        this.on('change', onChange);
        return {
            get pos() {
                return pos;
            },
            move: (delta) => {
                pos += delta;
            },
            destroy: () => {
                this.off('change', onChange);
            },
        };
    }
    dispatchTransaction(tr) {
        var _a;
        this.emit('predispatch', tr);
        if (((_a = this.dispatchHook) === null || _a === void 0 ? void 0 : _a.call(this, tr)) === false) {
            return;
        }
        const state = this.view.state.apply(tr);
        this.view.updateState(state);
        const evtData = new ChangeEvent(state, tr);
        this.emit('change', evtData);
        this.emit('dispatch', tr, evtData);
    }
    hookDispatch(hook, commands) {
        const oldHook = this.dispatchHook;
        this.dispatchHook = (tr) => hook(tr) === false ? false : oldHook.call(this, tr);
        commands.call(this);
        this.dispatchHook = oldHook;
    }
    createBatchedTr(commands) {
        const originalView = this.view;
        const batchTrs = [];
        let hasSetStoredMark = false;
        let hasSetSelection = false;
        let scrolledIntoView = false;
        this.view = new EditorView(null, {
            dispatchTransaction: (transaction) => {
                transaction.setMeta("ex:skipDecos" /* ExTrMetaKeys.skipDecos */, true);
                batchTrs.push(transaction);
                const state = this.view.state.apply(transaction);
                this.view.updateState(state);
            },
            state: originalView.state.reconfigure({
                plugins: [
                    new ProseMirrorPlugin({
                        props: {
                            handleScrollToSelection: () => (scrolledIntoView = true),
                        },
                    }),
                    ...originalView.state.plugins,
                ],
            }),
            domParser: DOMParser.fromSchema(originalView.state.schema),
        });
        commands.call(this);
        this.view = originalView;
        if (!batchTrs.length) {
            return null;
        }
        const { tr } = this.view.state;
        batchTrs.forEach((bufferedTr) => {
            bufferedTr.steps.forEach((step) => tr.step(step));
            hasSetStoredMark || (hasSetStoredMark = bufferedTr.storedMarksSet);
            hasSetSelection || (hasSetSelection = bufferedTr.selectionSet);
        });
        const lastTr = batchTrs.pop();
        if (hasSetStoredMark) {
            tr.setStoredMarks(lastTr.storedMarks);
        }
        if (hasSetSelection) {
            tr.setSelection(Selection.fromJSON(tr.doc, lastTr.selection.toJSON()));
        }
        if (scrolledIntoView) {
            tr.scrollIntoView();
        }
        return tr;
    }
}
//# sourceMappingURL=EditorCoreCommands.js.map